import type { GeneralEvent } from '@vlabs/api/luna-api/model';
import { AnalyticStreamLink } from '@vlabs/shared/components/stream-link/AnalyticStreamLink';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BaseEventPreview } from './BaseEventPreview';
import st from './BaseGeneralEventPreview.module.sass';
import { VideoAnalyticsImage } from '../../components/VideoAnalyticsImage';
import { getFormattedVaEventDateForCard } from '../../getFormattedVaEventDate';
import { selectNotifyEvents } from '../../selectors';
import { useEventInfoSettingsLocation } from '../../useEventInfoSettingsSearchParams';
import { useSelectedWorkspaceEvent } from '../../useSelectedWorkspaceEvent';

interface RenderTagsParams {
  isAlarm: boolean;
}
export interface BaseGeneralEventPreviewProps extends WithDataTestid {
  onClick?: (params: { event: GeneralEvent }) => void;
  event: GeneralEvent;
  renderTags?: (
    params: RenderTagsParams
  ) => React.ReactNode;
}

export function BaseGeneralEventPreview({
  event,
  onClick = undefined,
  renderTags = undefined,
}: BaseGeneralEventPreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const notifyEvents = useSelector(selectNotifyEvents);
  const { eventInfoSettings } = useEventInfoSettingsLocation();
  const { selectedEventId } = useSelectedWorkspaceEvent();

  const location = buildLocationString(event?.event);
  const date = getFormattedVaEventDateForCard(event?.create_time, event?.end_time);

  const isActive = selectedEventId === event?.event?.event_id;
  const isAlarm = notifyEvents.some((e) => e.event?.event_id === event?.event?.event_id);

  return (
    <BaseEventPreview
      data-testid={event?.event?.event_id}
      isActive={isActive}
      isAlarm={isAlarm}
      onClick={() => onClick({ event })}
    >
      <BaseEventPreview.Main>
        <BaseEventPreview.Media>
          <div className={st.Samples}>
            <VideoAnalyticsImage imageUrl={event?.event?.overview?.image} />
          </div>
        </BaseEventPreview.Media>

        <BaseEventPreview.Info>
          <div className={st.SamplesData}>
            {!!renderTags && (
              <div className={st.Tags}>
                {renderTags({ isAlarm })}
              </div>
            )}

            {!!eventInfoSettings.isVisibleDate && (
              <div
                className={st.InfoText}
                data-testid="workspace.analyticEventCard.createEndTime"
              >
                {date}
              </div>
            )}

            {!!eventInfoSettings.isVisibleSource && (
              <div
                className={cn(st.InfoText, st.InfoText_empty)}
                data-testid="workspace.analyticEventCard.sources"
              >
                <AnalyticStreamLink
                  className={st.StreamLink}
                  hasTruncatedLink
                  hideId
                  kind="secondary"
                  placeholders={{
                    removed: t('lunapoint:карточка события.videostream.removed'),
                    unavailable: t('lunapoint:карточка события.videostream.unavailable'),
                  }}
                  size="medium"
                  streamId={event?.event?.stream_id}
                />
              </div>
            )}
          </div>
        </BaseEventPreview.Info>
      </BaseEventPreview.Main>

      {!!eventInfoSettings.isVisibleAddress && (
        <BaseEventPreview.Footer>
          <div
            className={cn(st.InfoText, {
              [st.InfoText_empty]: location === t('lunapoint:адрес отсутствует'),
            })}
            data-testid="workspace.analyticEventCard.location"
          >
            {location}
          </div>
        </BaseEventPreview.Footer>
      )}
    </BaseEventPreview>
  );
}
